/** Instance-owned fuel extracted from the separately maintained Steam Punk game.
 * The host supplies clock, randomness, DOM and lifecycle; no globals or score API are shared. */
export function installSteamFuel(runtime) {
  runtime.neutronFuelPanelRect = function () {
    return { x: 66, y: runtime.LAUNCH_HUD_Y - 40, w: 208, h: 78 };
  };
  runtime.isPointInNeutronFuelPanel = function (x, y) {
    const rect = runtime.neutronFuelPanelRect();
    return x >= rect.x && x <= rect.x + rect.w && y >= rect.y && y <= rect.y + rect.h;
  };
  runtime.drawNeutronFuelTankPanel = function (cx, cy, timeSeconds) {
    const rect = runtime.neutronFuelPanelRect();
    const ready = runtime.canActivateNeutronBombs();
    const fill = runtime.clamp(
      runtime.game.neutronFuel / Math.max(1, runtime.game.neutronFuelCost),
      0,
      1,
    );
    const pulse = ready ? 0.5 + Math.sin(timeSeconds * 7.2) * 0.5 : 0;
    const sprite = runtime.getNeutronFuelGaugeSprite();
    runtime.ctx.save();
    runtime.ctx.globalAlpha = 0.9;
    runtime.ctx.shadowColor = ready ? 'rgba(255, 95, 72, 0.9)' : 'rgba(116, 255, 220, 0.24)';
    runtime.ctx.shadowBlur = ready ? 20 + pulse * 18 : 10;
    runtime.ctx.drawImage(sprite, rect.x, rect.y);
    const glass = runtime.neutronFuelGlassRect(rect);
    runtime.ctx.save();
    runtime.roundRectCtx(runtime.ctx, glass.x, glass.y, glass.w, glass.h, 10);
    runtime.ctx.clip();
    runtime.ctx.fillStyle = 'rgba(4, 16, 18, 0.84)';
    runtime.ctx.fillRect(glass.x, glass.y, glass.w, glass.h);
    const fillGradient = runtime.ctx.createLinearGradient(glass.x, 0, glass.x + glass.w, 0);
    fillGradient.addColorStop(0, ready ? '#fffefa' : '#1dbad5');
    fillGradient.addColorStop(0.44, ready ? '#ffd166' : '#71f1d1');
    fillGradient.addColorStop(1, ready ? '#ff3f9f' : '#73ff85');
    runtime.ctx.fillStyle = fillGradient;
    runtime.ctx.fillRect(glass.x, glass.y, Math.max(3, glass.w * fill), glass.h);
    runtime.ctx.globalCompositeOperation = 'lighter';
    runtime.ctx.globalAlpha = 0.36 + fill * 0.24;
    runtime.ctx.fillStyle = ready ? 'rgba(255, 248, 240, 0.72)' : 'rgba(158, 244, 213, 0.45)';
    runtime.ctx.fillRect(glass.x, glass.y, glass.w * fill, Math.max(3, glass.h * 0.32));
    runtime.ctx.restore();
    runtime.drawNeutronFuelGlassOverlay(glass, ready, pulse);
    runtime.drawNeutronFuelPlaques(rect, ready);
    runtime.drawNeutronFuelButton(rect, ready, pulse);
    runtime.ctx.restore();
  };
  runtime.neutronFuelGlassRect = function (rect) {
    return { x: rect.x + 30, y: rect.y + 27, w: 108, h: 20 };
  };
  runtime.getNeutronFuelGaugeSprite = function () {
    const key = 'copper-gauge-v1';
    if (runtime.neutronFuelGaugeSpriteCache.has(key))
      return runtime.neutronFuelGaugeSpriteCache.get(key);
    const rect = runtime.neutronFuelPanelRect();
    const sprite = document.createElement('canvas');
    sprite.width = rect.w;
    sprite.height = rect.h;
    const target = sprite.getContext('2d');
    runtime.drawNeutronGaugeFrame(target, rect.w, rect.h);
    runtime.neutronFuelGaugeSpriteCache.set(key, sprite);
    return sprite;
  };
  runtime.drawNeutronGaugeFrame = function (target, w, h) {
    const copper = target.createLinearGradient(0, 0, 0, h);
    copper.addColorStop(0, 'rgba(255, 202, 128, 0.96)');
    copper.addColorStop(0.12, 'rgba(158, 84, 33, 0.98)');
    copper.addColorStop(0.48, 'rgba(80, 35, 14, 0.98)');
    copper.addColorStop(0.74, 'rgba(188, 103, 38, 0.98)');
    copper.addColorStop(1, 'rgba(42, 17, 7, 0.98)');
    target.save();
    target.shadowColor = 'rgba(0, 0, 0, 0.5)';
    target.shadowBlur = 12;
    target.shadowOffsetY = 5;
    runtime.roundRectCtx(target, 3, 5, w - 6, h - 10, 18);
    target.fillStyle = copper;
    target.fill();
    target.strokeStyle = 'rgba(255, 229, 166, 0.76)';
    target.lineWidth = 2;
    target.stroke();
    target.shadowBlur = 0;
    const inset = target.createLinearGradient(0, 8, 0, h - 10);
    inset.addColorStop(0, 'rgba(255, 232, 176, 0.16)');
    inset.addColorStop(0.38, 'rgba(38, 14, 5, 0.58)');
    inset.addColorStop(1, 'rgba(255, 161, 70, 0.15)');
    runtime.roundRectCtx(target, 10, 12, w - 20, h - 24, 13);
    target.fillStyle = inset;
    target.fill();
    target.strokeStyle = 'rgba(44, 15, 5, 0.76)';
    target.lineWidth = 2;
    target.stroke();
    runtime.drawGaugeRivets(target, w, h);
    runtime.drawGaugeGlassFrame(target);
    runtime.drawButtonSocket(target, w);
    target.restore();
  };
  runtime.drawGaugeRivets = function (target, w, h) {
    const rivets = [
      [17, 18],
      [17, h - 18],
      [w - 17, 18],
      [w - 17, h - 18],
      [49, 14],
      [111, 14],
      [49, h - 14],
      [111, h - 14],
    ];
    for (const [x, y] of rivets) {
      const rivet = target.createRadialGradient(x - 2, y - 2, 1, x, y, 6);
      rivet.addColorStop(0, '#ffe6ad');
      rivet.addColorStop(0.35, '#c7772f');
      rivet.addColorStop(1, '#3a1506');
      target.beginPath();
      target.arc(x, y, 4.8, 0, Math.PI * 2);
      target.fillStyle = rivet;
      target.fill();
      target.strokeStyle = 'rgba(33, 11, 3, 0.78)';
      target.lineWidth = 1;
      target.stroke();
    }
  };
  runtime.drawGaugeGlassFrame = function (target) {
    const frame = target.createLinearGradient(24, 22, 24, 52);
    frame.addColorStop(0, '#ffe8b4');
    frame.addColorStop(0.24, '#b56b2d');
    frame.addColorStop(0.72, '#3b1507');
    frame.addColorStop(1, '#d98b3f');
    runtime.roundRectCtx(target, 23, 21, 122, 32, 15);
    target.fillStyle = frame;
    target.fill();
    target.strokeStyle = 'rgba(255, 237, 188, 0.72)';
    target.lineWidth = 1.4;
    target.stroke();
    runtime.roundRectCtx(target, 30, 27, 108, 20, 10);
    target.strokeStyle = 'rgba(255, 244, 202, 0.44)';
    target.lineWidth = 1.2;
    target.stroke();
  };
  runtime.drawButtonSocket = function (target, w) {
    const socket = target.createRadialGradient(w - 37, 38, 6, w - 37, 38, 32);
    socket.addColorStop(0, 'rgba(80, 25, 10, 0.92)');
    socket.addColorStop(0.58, 'rgba(28, 9, 4, 0.96)');
    socket.addColorStop(1, 'rgba(232, 139, 55, 0.58)');
    target.beginPath();
    target.arc(w - 37, 38, 28, 0, Math.PI * 2);
    target.fillStyle = socket;
    target.fill();
    target.strokeStyle = 'rgba(255, 218, 145, 0.54)';
    target.lineWidth = 2;
    target.stroke();
  };
  runtime.drawNeutronFuelGlassOverlay = function (glass, ready, pulse) {
    runtime.ctx.save();
    runtime.roundRectCtx(runtime.ctx, glass.x, glass.y, glass.w, glass.h, 10);
    const sheen = runtime.ctx.createLinearGradient(0, glass.y, 0, glass.y + glass.h);
    sheen.addColorStop(0, 'rgba(255, 255, 255, 0.52)');
    sheen.addColorStop(0.36, 'rgba(255, 255, 255, 0.08)');
    sheen.addColorStop(0.72, 'rgba(0, 0, 0, 0.18)');
    sheen.addColorStop(1, 'rgba(255, 255, 255, 0.18)');
    runtime.ctx.fillStyle = sheen;
    runtime.ctx.fill();
    runtime.ctx.strokeStyle = ready
      ? `rgba(255, 248, 240, ${0.72 + pulse * 0.18})`
      : 'rgba(190, 255, 232, 0.45)';
    runtime.ctx.lineWidth = ready ? 2 : 1.2;
    runtime.ctx.stroke();
    runtime.ctx.restore();
  };
  runtime.drawNeutronFuelPlaques = function (rect, ready) {
    runtime.drawGaugePlaque(rect.x + 35, rect.y + 8, 68, 15, 'FUEL', ready);
    runtime.drawGaugePlaque(
      rect.x + 47,
      rect.y + 55,
      72,
      16,
      `${runtime.game.neutronFuel}/${runtime.game.neutronFuelCost}`,
      ready,
    );
  };
  runtime.drawGaugePlaque = function (x, y, w, h, text, ready) {
    const plaque = runtime.ctx.createLinearGradient(0, y, 0, y + h);
    plaque.addColorStop(0, 'rgba(255, 224, 162, 0.94)');
    plaque.addColorStop(0.45, 'rgba(92, 39, 13, 0.94)');
    plaque.addColorStop(1, 'rgba(30, 11, 4, 0.94)');
    runtime.ctx.save();
    runtime.ctx.shadowColor = ready ? 'rgba(255, 248, 240, 0.55)' : 'rgba(0, 0, 0, 0.42)';
    runtime.ctx.shadowBlur = ready ? 9 : 4;
    runtime.roundRectCtx(runtime.ctx, x, y, w, h, 5);
    runtime.ctx.fillStyle = plaque;
    runtime.ctx.fill();
    runtime.ctx.strokeStyle = 'rgba(255, 230, 170, 0.72)';
    runtime.ctx.lineWidth = 1;
    runtime.ctx.stroke();
    runtime.ctx.shadowBlur = 0;
    runtime.ctx.fillStyle = ready ? '#fff8f0' : '#ffe1a2';
    runtime.ctx.font = `900 ${text.includes('/') ? 12 : 9}px Orbitron, Arial, sans-serif`;
    runtime.ctx.textAlign = 'center';
    runtime.ctx.textBaseline = 'middle';
    runtime.ctx.fillText(text, x + w / 2, y + h / 2 + 0.5);
    runtime.ctx.restore();
  };
  runtime.drawNeutronFuelButton = function (rect, ready, pulse) {
    const cx = rect.x + rect.w - 37;
    const cy = rect.y + 39;
    const radius = ready ? 22 + pulse * 1.8 : 20;
    runtime.ctx.save();
    runtime.ctx.shadowColor = ready ? 'rgba(255, 67, 72, 0.92)' : 'rgba(0, 0, 0, 0.48)';
    runtime.ctx.shadowBlur = ready ? 18 + pulse * 18 : 7;
    const face = runtime.ctx.createRadialGradient(cx - 7, cy - 8, 3, cx, cy, radius);
    face.addColorStop(0, ready ? '#fff7d7' : '#c4864b');
    face.addColorStop(0.26, ready ? '#ffcf66' : '#8b4b23');
    face.addColorStop(0.58, ready ? '#d7222f' : '#4a1d0c');
    face.addColorStop(1, ready ? '#5a0610' : '#1c0803');
    runtime.ctx.beginPath();
    runtime.ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    runtime.ctx.fillStyle = face;
    runtime.ctx.fill();
    runtime.ctx.strokeStyle = ready
      ? `rgba(255, 248, 240, ${0.78 + pulse * 0.18})`
      : 'rgba(255, 198, 126, 0.42)';
    runtime.ctx.lineWidth = ready ? 3 : 2;
    runtime.ctx.stroke();
    runtime.ctx.shadowBlur = 0;
    runtime.ctx.globalAlpha = ready ? 1 : 0.5;
    runtime.ctx.fillStyle = ready ? '#fff8f0' : '#d6a06a';
    runtime.ctx.font = '900 10px Orbitron, Arial, sans-serif';
    runtime.ctx.textAlign = 'center';
    runtime.ctx.textBaseline = 'middle';
    runtime.ctx.fillText('FIRE', cx, cy + 0.5);
    runtime.ctx.restore();
  };
  runtime.drawAim = function () {
    const aim = runtime.trigDeg(runtime.game.angle);
    runtime.ctx.save();
    runtime.ctx.strokeStyle = 'rgba(255,209,102,0.78)';
    runtime.ctx.shadowColor = '#ffd166';
    runtime.ctx.shadowBlur = 10;
    runtime.ctx.lineWidth = 2.2;
    runtime.ctx.setLineDash([14, 12, 3, 12]);
    runtime.ctx.beginPath();
    runtime.ctx.moveTo(
      runtime.launcher.x + aim.cos * runtime.LAUNCHER_MUZZLE_DISTANCE,
      runtime.launcher.y - aim.sin * runtime.LAUNCHER_MUZZLE_DISTANCE,
    );
    runtime.ctx.lineTo(runtime.launcher.x + aim.cos * 260, runtime.launcher.y - aim.sin * 260);
    runtime.ctx.stroke();
    runtime.ctx.setLineDash([]);
    runtime.ctx.restore();
  };
}
